import { useCallback, useRef } from 'react';
import usePDA1120_R01Store from './usePDA1120_R01Store';
import {
  getPDA1120_R01List,
  getPDA1120_R01DetailList,
  getPDA1120_R01One,
  postPDA1120_R01,
} from 'src/apis/materialCheckApi';
import { useToast } from 'src/hooks/useToast';
import { PDA1120_R01Dto } from 'src/containers/PDA/PDA1120_R01/PDA1120_R01Dto';
import { resolveFeeder } from './feederUtils';

const PDA1120_R01Content = () => {
  const {
    setLoading,
    searchParams,
    setOrderList,
    selectedOrder,
    setSelectedOrder,
    feederList,
    setFeederList,
    barcode,
    scannedLot,
    setScannedLot,
    resetScanForm,
    checkList,
    localCheckedQty,
    addToCheckList,
    removeFromCheckList,
    clearCheckList,
  } = usePDA1120_R01Store();

  const { success, error, warning } = useToast();
  // LOT 스캔 중복 방지를 위한 ref
  const isScanningRef = useRef(false);
  const lastScannedLotRef = useRef<string>('');

  /**
   * 공정지시 목록 조회
   */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPDA1120_R01List(searchParams);

      if (response.data.successFlag && response.data.data) {
        setOrderList(response.data.data);
        success(`${response.data.data.length}건의 공정지시를 조회했습니다.`);
      } else {
        setOrderList([]);
        warning('조회된 공정지시가 없습니다.');
      }
    } catch (err: any) {
      console.error('공정지시 목록 조회 실패:', err);
      error(err?.response?.data?.detailMessage || err?.response?.data?.message || '공정지시 목록 조회 중 오류가 발생했습니다.');
      setOrderList([]);
    } finally {
      setLoading(false);
    }
  }, [searchParams, setLoading, setOrderList, success, warning, error]);

  /**
   * 공정지시 선택 (공정지시 그리드 클릭)
   */
  const fetchDetailTableData = useCallback(
    async (order: PDA1120_R01Dto) => {
      if (selectedOrder?.productWorkNo === order.productWorkNo) {
        return; // 이미 선택된 공정지시
      }

      setLoading(true);
      try {
        setSelectedOrder(order);

        // 피더 목록 조회 (품목+피더 단위 체크를 위해 공정코드 전달)
        const feederResponse = await getPDA1120_R01DetailList(
          order.productWorkNo!,
          order.workplaceCd ?? undefined
        );

        if (feederResponse.data.successFlag && feederResponse.data.data) {
          setFeederList(feederResponse.data.data);
        } else {
          setFeederList([]);
          warning('조회된 피더가 없습니다.');
        }

        // 스캔 폼 초기화
        resetScanForm();
      } catch (err: any) {
        console.error('피더 목록 조회 실패:', err);
        error(err?.response?.data?.detailMessage || err?.response?.data?.message || '피더 목록 조회 중 오류가 발생했습니다.');
        setFeederList([]);
      } finally {
        setLoading(false);
      }
    },
    [
      selectedOrder,
      setLoading,
      setSelectedOrder,
      setFeederList,
      resetScanForm,
      warning,
      error,
    ]
  );


  /**
   * LOT 스캔 - feederCd 없으면 차단
   * 이미 실장된 피더(checkList)는 백엔드에 전달해 미실장 피더만 반환받음.
   * @param lotNo LOT 번호 (옵션, 없으면 barcode 상태 사용)
   */
  const fetchInfoData = useCallback(async (lotNo?: string): Promise<boolean> => {
    if (!selectedOrder) {
      warning('공정지시를 먼저 선택하세요.');
      return false;
    }

    const lotNoToScan = lotNo || barcode;
    if (!lotNoToScan || lotNoToScan.trim() === '') {
      warning('LOT 번호를 입력하세요.');
      return false;
    }

    const trimmedLot = lotNoToScan.trim();
    if (isScanningRef.current || lastScannedLotRef.current === trimmedLot) {
      return false;
    }

    isScanningRef.current = true;
    lastScannedLotRef.current = trimmedLot;
    setLoading(true);

    try {
      const checkedFeeders = checkList.map((item) => item.feederCd).filter(Boolean);

      const response = await getPDA1120_R01One(
        trimmedLot,
        selectedOrder.productWorkNo!,
        checkedFeeders
      );

      if (response.data.successFlag && response.data.data) {
        const lotInfo = response.data.data;

        if (!lotInfo.feederCd || lotInfo.feederCd.trim() === '') {
          error('피더 정보가 없는 자재입니다. 실장 좌표를 확인하세요.');
          setScannedLot(null);
          return false;
        }

        setScannedLot(lotInfo);
        success('품목이 일치합니다. 기계를 스캔해주세요.');
        return true;
      } else {
        setScannedLot(null);
        return false;
      }
    } catch {
      // 에러 메시지는 axios 인터셉터에서 이미 표시됨 (중복 알림 방지)
      setScannedLot(null);
      return false;
    } finally {
      setLoading(false);
      isScanningRef.current = false;
      setTimeout(() => { lastScannedLotRef.current = ''; }, 1000);
    }
  }, [
    selectedOrder,
    barcode,
    checkList,
    setLoading,
    setScannedLot,
    success,
    warning,
    error,
  ]);

  

  /**
   * 재조회 (캐시 무시)
   */
  const handleRefetchOrders = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // ========== 새로운 함수들 ==========
  
  /**
   * 실장 버튼
   * - 같은 LOT 중복 실장 불가
   * - 필요량 기준으로 동일 품목 피더를 테이블 순서대로 순차 배분
   */
  const handleMount = useCallback(() => {
    if (!selectedOrder) {
      warning('공정지시를 먼저 선택하세요.');
      return;
    }
    if (!scannedLot) {
      warning('먼저 LOT를 스캔해주세요.');
      return;
    }
    if (!scannedLot.matched) {
      error('품목이 일치하지 않습니다.');
      return;
    }
    if (checkList.some((c) => c.lotNo === scannedLot.lotNo)) {
      warning('이미 실장된 LOT입니다.');
      return;
    }

    // 품목별 피더 목록 조회
    const itemRows = feederList.filter((f) => f.childItemCd === scannedLot.itemCd);
    // 품목별 피더 목록이 없으면 오류 메시지 출력
    if (itemRows.length === 0) {
      error(`품목(${scannedLot.itemNm})과 일치하는 피더 항목이 없습니다.`);
      return;
    }

    // 남은 수량 조회
    let remainingQty = Number(scannedLot.lotQty) || 0;
    // 남은 수량이 0보다 작으면 오류 메시지 출력
    if (remainingQty <= 0) {
      warning('실장 가능한 수량이 없습니다.');
      return;
    }

    let allocatedQty = 0;
    let allocatedCount = 0;
    // 품목별 피더 목록 처리
    for (const row of itemRows) {
      if (remainingQty <= 0) break;
      // 피더 코드 조회
      const feederCd = resolveFeeder(row, selectedOrder.workplaceCd);
      if (!feederCd) continue;
      // 필요량 조회
      const requiredQty = Number(row.childItemUsage) || 0;
      const feederKey = `${feederCd}_${row.childItemCd}`;
      // DB 실장량 조회
      const dbMountedQty = Number(row.lotQty) || 0;
      // 로컬 실장량 조회
      const localMountedQty = localCheckedQty[feederKey] || 0;
      // 남은 수량 조회
      const rowRemainingQty =
        requiredQty > 0 ? Math.max(requiredQty - dbMountedQty - localMountedQty, 0) : 0;
      // 남은 수량이 0보다 작으면 다음 피더로 이동
      if (rowRemainingQty <= 0) continue;
      // 실장 수량 조회
      const mountQty = Math.min(remainingQty, rowRemainingQty);
      if (mountQty <= 0) continue;
      // 실장 목록에 추가
      addToCheckList({
        lotNo: scannedLot.lotNo,
        itemCd: scannedLot.itemCd,
        itemNm: scannedLot.itemNm,
        itemSpec: scannedLot.itemSpec,
        lotQty: mountQty,
        feederCd,
      });

     // 실장 수량 추가
      allocatedQty += mountQty;
      // 실장 피더 개수 추가
      allocatedCount += 1;
      // 남은 수량 추가
      remainingQty -= mountQty;
    }
    // 실장 수량이 0보다 작으면 오류 메시지 출력
    if (allocatedQty <= 0) {
      warning('해당 품목의 모든 피더가 이미 필요량을 충족했습니다.');
      return;
    }

    // if (remainingQty > 0) {
    //   warning(`잔여 필요량 부족으로 ${remainingQty.toLocaleString()}개는 제외되었습니다.`);
    // }

    success(
      `${scannedLot.itemNm} 실장 준비 완료 (${allocatedQty.toLocaleString()}) / 피더 ${allocatedCount}건`
    );

    if (allocatedQty > 0) {
      resetScanForm();
      usePDA1120_R01Store.getState().resetMachineScan();
    }
  }, [
    selectedOrder,
    scannedLot,
    feederList,
    checkList,
    localCheckedQty,
    addToCheckList,
    resetScanForm,
    success,
    warning,
    error,
  ]);

  /**
   * [완료] 버튼: 일괄 DB 저장
   */
  const handleSaveClick = useCallback(async () => {
    if (!selectedOrder) {
      warning('공정지시를 선택하세요.');
      return;
    }
    
    if (checkList.length === 0) {
      warning('실장할 항목이 없습니다.');
      return;
    }
    
    setLoading(true);
    try {
      // checkList를 요청 데이터로 변환
      const requests = checkList.map(item => ({
        productWorkNo: selectedOrder.productWorkNo!,
        workDt: selectedOrder.workDt || selectedOrder.orderDt!,
        workplaceCd: selectedOrder.workplaceCd!,
        lotNo: item.lotNo,
        itemCd: item.itemCd,
        lotQty: item.lotQty,
        feederCd: item.feederCd,
        remark: ''
      }));
      
      // 일괄 저장 API 호출
      const response = await postPDA1120_R01(requests);
      
      if (response.data.successFlag) {
        success(
          response.data.data || 
          `${checkList.length}개 항목이 저장되었습니다.`
        );
        
        
        // checkList 초기화
        clearCheckList();
        
        // 피더 목록 재조회 (실제 DB 데이터로 업데이트, 품목+피더 단위)
        const feederResponse = await getPDA1120_R01DetailList(
          selectedOrder.productWorkNo!,
          selectedOrder.workplaceCd ?? undefined
        );
        if (feederResponse.data.successFlag && feederResponse.data.data) {
          console.log('  피더 목록 재조회 완료:', feederResponse.data.data.length);
          setFeederList(feederResponse.data.data);
        }
      }
    } catch (err: any) {
      console.error('일괄 저장 실패:', err);
      error(
        err?.response?.data?.detailMessage || 
        err?.response?.data?.message || 
        '저장 중 오류가 발생했습니다.'
      );
    } finally {
      setLoading(false);
    }
  }, [
    selectedOrder,
    checkList,
    setLoading,
    clearCheckList,
    setFeederList,
    success,
    warning,
    error
  ]);

  /**
   * checkList 항목 제거
   */
  const handleRemoveCheckItem = useCallback((lotNo: string) => {
    removeFromCheckList(lotNo);
    success('항목이 제거되었습니다.');
  }, [removeFromCheckList, success]);

  /**
   * checkList 전체 취소
   */
  const handleCancelAll = useCallback(() => {
    if (checkList.length === 0) {
      warning('취소할 항목이 없습니다.');
      return;
    }
    
    if (window.confirm('모든 실장 대기 항목을 취소하시겠습니까?')) {
      usePDA1120_R01Store.getState().clearCheckList();
      success('모든 항목이 취소되었습니다.');
    }
  }, [checkList, success, warning]);
    // 뒤로가기 (공정지시 선택 해제)
    const handleBackToList = () => {
      setSelectedOrder(null);
    };

    

  // 체크 완료 개수
  const checkedCount = feederList.filter((f) => f.checkStatus === 'Y').length;

  return {
    fetchData,
    handleRefetchOrders,
    fetchDetailTableData,
    fetchInfoData,
    handleMount,
    handleSaveClick,
    handleRemoveCheckItem,
    handleCancelAll,
    handleBackToList,
    checkedCount,
  };
};

export default PDA1120_R01Content;

